import { apiClient } from './client';
import type {
  License,
  LicenseWithDetails,
  CreateLicenseInput,
  CreateTrialLicenseInput,
  UpdateLicenseInput,
  LicenseSearchFilters,
  BulkStatusUpdateInput,
  BulkStatusUpdateResult,
} from '../../types';

export const licensesService = {
  search: async (filters: LicenseSearchFilters = {}): Promise<{ licenses: LicenseWithDetails[] }> => {
    return apiClient.get<{ licenses: LicenseWithDetails[] }>('/api/v1/licenses', filters);
  },

  getById: async (id: string): Promise<License> => {
    return apiClient.get<License>(`/api/v1/licenses/${id}`);
  },

  getByEid: async (eid: string): Promise<LicenseWithDetails> => {
    return apiClient.get<LicenseWithDetails>(`/api/v1/licenses/eid/${eid}`);
  },

  checkEid: async (eid: string): Promise<{ eid: string; exists: boolean }> => {
    return apiClient.get<{ eid: string; exists: boolean }>(`/api/v1/licenses/check-eid/${eid}`);
  },

  getStats: async (): Promise<{ total: number; active: number; draft: number; suspended: number; expired: number; revoked: number }> => {
    return apiClient.get('/api/v1/licenses/stats');
  },

  getExpiring: async (days?: number): Promise<{ licenses: LicenseWithDetails[]; expiringWithinDays: number }> => {
    return apiClient.get<{ licenses: LicenseWithDetails[]; expiringWithinDays: number }>(
      '/api/v1/licenses/expiring',
      days ? { days } : undefined
    );
  },

  create: async (input: CreateLicenseInput): Promise<License> => {
    return apiClient.post<License>('/api/v1/licenses', input);
  },

  createTrial: async (input: CreateTrialLicenseInput): Promise<License> => {
    return apiClient.post<License>('/api/v1/licenses/trial', input);
  },

  update: async (id: string, input: UpdateLicenseInput): Promise<License> => {
    return apiClient.patch<License>(`/api/v1/licenses/${id}`, input);
  },

  revoke: async (id: string, reason: string): Promise<License> => {
    return apiClient.delete<License>(`/api/v1/licenses/${id}`, { reason });
  },

  suspend: async (id: string, reason?: string): Promise<License> => {
    return apiClient.post<License>(`/api/v1/licenses/${id}/suspend`, { reason });
  },

  reactivate: async (id: string): Promise<License> => {
    return apiClient.post<License>(`/api/v1/licenses/${id}/reactivate`);
  },

  bulkUpdateStatus: async (input: BulkStatusUpdateInput): Promise<BulkStatusUpdateResult> => {
    return apiClient.post<BulkStatusUpdateResult>('/api/v1/licenses/bulk-status', input);
  },

  exportCurrentLicenses: async (): Promise<Blob> => {
    return apiClient.getBlob('/api/v1/licenses/export/current');
  },

  exportActivationHistory: async (id: string): Promise<Blob> => {
    return apiClient.getBlob(`/api/v1/licenses/${id}/export/activation-history`);
  },
};
